import { createSlice } from '@reduxjs/toolkit';
import ApplicationListActions from 'modules/application/list/applicationListActions';
import ApplicationListSelectors from 'modules/application/list/applicationListSelectors';
import {
  ENVIRONMENT_SANDBOX,
  ENVIRONMENT_PRODUCTION,
  ENVIRONMENT_PRE_LIVE,
} from 'utils/constants';
import authSelectors from 'modules/auth/authSelectors';
import PermissionChecker from 'modules/auth/permissionChecker';
import { getHistory } from 'modules/history';
import featureFlags, { FeatureFlagsAttributes } from 'modules/featureFlags';
import { reloadApp } from './bootstrap';
import { privateRoutes } from '../view/routes';

const initialState = {
  environment: '',
  appToken: '',
};

export const globalSlice = createSlice({
  name: 'global',
  initialState,
  reducers: {
    updateEnvironment: (state, action) => {
      state.environment = action.payload;
    },
    updateAppToken: (state, action) => {
      state.appToken = action.payload;
    },
  },
});

// APIs
const ENVIRONMENT_LOCAL_STORAGE_KEY = '@GLOBAL/ENVIRONMENT';

const getEnvironmentFromLocalStorage = async () =>
  localStorage.getItem(ENVIRONMENT_LOCAL_STORAGE_KEY);

const setEnvironmentToLocalStorage = async value =>
  localStorage.setItem(ENVIRONMENT_LOCAL_STORAGE_KEY, value);

// Action creators
const { updateEnvironment, updateAppToken } = globalSlice.actions;

const getDefaultEnvironment = applications => {
  const { sandbox, production, preLive } = applications;

  if (sandbox.length > 0) return ENVIRONMENT_SANDBOX;
  if (preLive.length > 0) return ENVIRONMENT_PRE_LIVE;
  if (production.length > 0) return ENVIRONMENT_PRODUCTION;

  return ENVIRONMENT_SANDBOX;
};

const getAppIdForEnvironmentFromAppList = ({
  environment,
  applicationList,
}) => {
  const appId =
    applicationList &&
    applicationList[environment] &&
    applicationList[environment][0] &&
    applicationList[environment][0].id;

  return appId || '';
};

// Actions
export const setAppToken = value => async dispatch => {
  dispatch(updateAppToken(value));

  featureFlags.setAttributes({
    [FeatureFlagsAttributes.appToken]: value,
  });
};

export const setEnvironment = value => async dispatch => {
  dispatch(updateEnvironment(value));

  featureFlags.setAttributes({
    [FeatureFlagsAttributes.accessLevel]: value,
  });
};

export const setEnvironmentInitialValue =
  isToggleEnvironment => async (dispatch, getState) => {
    const applicationList = ApplicationListSelectors.selectApplicationList(
      getState(),
    );
    const { pathname } = getState().router.location;
    const pathnameArray = pathname.split('/');

    // Resolve a candidate environment in priority order:
    // 1. App ID in the current URL (skipped when toggling environment)
    // 2. Value stored in localStorage
    // 3. Value already in redux state
    // 4. First available environment derived from loaded apps
    let candidate;

    if (pathnameArray[1] === 'id' && !isToggleEnvironment) {
      const appIdFromURL = pathnameArray[2];
      const envFromURL = dispatch(
        ApplicationListActions.getApplicationEnvironment(appIdFromURL),
      );
      if (envFromURL) {
        candidate = envFromURL;
        await setEnvironmentToLocalStorage(envFromURL);
      }
    }

    if (!candidate) {
      candidate = (await getEnvironmentFromLocalStorage()) || undefined;
    }

    if (!candidate) {
      candidate = selectEnvironment(getState()) || undefined;
    }

    if (!candidate) {
      candidate = getDefaultEnvironment(applicationList);
    }

    // Validate the candidate has a corresponding app (e.g. sandbox may be
    // absent in a DR setup). Fall back to the first available env if not.
    const hasApp = !!getAppIdForEnvironmentFromAppList({
      environment: candidate,
      applicationList,
    });
    const environment = hasApp ? candidate : getDefaultEnvironment(applicationList);

    const appId = getAppIdForEnvironmentFromAppList({ environment, applicationList });
    dispatch(setEnvironment(environment));
    dispatch(setAppToken(appId));
    await setEnvironmentToLocalStorage(environment);
  };

export const toggleEnvironmentValue =
  ({ currentRoutePath = '', environment } = {}) =>
  async (dispatch, getState) => {
    const application = ApplicationListSelectors.selectApplication(getState());
    const applicationList = ApplicationListSelectors.selectApplicationList(
      getState(),
    );
    const { router, root } = getState();
    const { pathname } = router.location;
    const { auth } = root;
    const { currentUser } = auth;

    const targetApplicationId = getAppIdForEnvironmentFromAppList({
      environment,
      applicationList,
    });

    // check if user has access to the same current page on the other environment
    const permissionChecker = new PermissionChecker(
      currentUser,
      authSelectors.selectScopes(getState()),
      environment,
    );

    const isPageAccessibleOnNewEnvironment =
      permissionChecker.isPageAccessibleOnEnvironment(pathname);

    const currentRoute = privateRoutes.find(
      route => route.path === currentRoutePath,
    );

    if (isPageAccessibleOnNewEnvironment && currentRoute?.isDetailsPage) {
      // go back to the parent page or the first accessible page
      getHistory().push(
        permissionChecker.getParentPage(application?.id, currentRoutePath),
      );
    }

    if (!isPageAccessibleOnNewEnvironment) {
      // if isPageAccessibleOnNewEnvironment is false, redirect to first accessible page from menu
      getHistory().push(
        permissionChecker.getFirstAccessiblePage(application?.id),
      );
    }

    // update environment value in local storage and redux store
    await setEnvironmentToLocalStorage(environment);
    dispatch(setEnvironment(environment));
    dispatch(setAppToken(targetApplicationId));

    await dispatch(reloadApp());
  };

// Selectors
export const selectEnvironment = state => state.root.global.environment;
export const selectAppToken = state => state.root.global.appToken;

export default globalSlice.reducer;
